import {
  CSRF_HEADER,
  changeKindOf,
  changeRequestState,
  isFolderOp,
  MAX_MESSAGE_CHARS,
  type ProjectDetail,
  trustState,
  type WikiChangeItem,
  type WikiChangeSet,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bot, Check, Eye, FolderTree, Pencil, Send, TextQuote, X } from "lucide-react";
import { type Ref, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { api } from "../api.ts";
import { noContent, ok } from "../api-errors.ts";
import { canChange } from "../projects/capabilities.ts";
import { navigate, projectConversationPath, withOpenWiki } from "../router.tsx";
import { useSession } from "../session.tsx";
import { AuthorPublishSheet } from "./AuthorPublishSheet.tsx";
import { ChangeSetPublishDialog } from "./ChangeSetPublishDialog.tsx";
import { ChangeItemEditor, ItemContent, itemName } from "./ChangeSetReview.tsx";
import { instructionsMessage, queueInstructions, useInstructionTray } from "./instructions.ts";
import { MarkedPage, type ReadingView } from "./MarkedPage.tsx";
import { useChangeItem, useReviewQueue, wikiKeys } from "./queries.ts";
import { ResolutionReview } from "./ResolutionReview.tsx";
import { ResolveTrigger } from "./ResolutionRunCard.tsx";
import { flagOf } from "./ReviewReader.tsx";
import { queueResolve } from "./resolve.ts";
import { SelectionPopover } from "./SelectionPopover.tsx";
import { itemSize } from "./sizes.ts";
import { ThreadPanel } from "./ThreadPanel.tsx";
import { useReviewGuard } from "./use-review-guard.tsx";
import { whoDecides } from "./WikiChangesCard.tsx";

const STRUCTURE = "structure";
/**
 * Whether `viewer` meets `set` as its author's preview (spec `project-wiki` "The author's own
 * set shows no reviewer actions"): their own open set, whatever its origin (the agent in a
 * conversation, a person, an answer, a restore or revert, an MCP client). Instructions to the
 * agent exist only for a set with a conversation; declassification stays in the full dialog.
 */
export function authorPreviewOf(
  set: Pick<WikiChangeSet, "createdBy" | "status">,
  viewer: string,
): boolean {
  return (
    set.createdBy.toLowerCase() === viewer.toLowerCase() &&
    (set.status === "open" || set.status === "changes_requested")
  );
}

export const isStructureItem = (item: Pick<WikiChangeItem, "op">) =>
  ["moves", "folders"].includes(changeKindOf(item.op));

/**
 * The creator's live preview of their own change set (wiki-collaboration-ux D1, D3; mock S2),
 * in the side panel and on the review URL: each changed page rendered as it will read with its
 * changes marked, the structure as one before → after list, and author actions only (Publish
 * when the trust level allows, else Submit for review; Discard; Instruct the agent). Once
 * submitted it is read-only, with Approve and publish only when the trust level lets the creator
 * decide their own set. No Approve, Reject or Request changes otherwise.
 */
export function AuthorPreview(props: {
  project: ProjectDetail;
  set: WikiChangeSet;
  /** The selected item, or `structure`; null selects the first. */
  selected: string | null;
  onSelect: (selected: string) => void;
  view: ReadingView;
  onView: (view: ReadingView) => void;
  /** Beside the conversation: Send hands the message to its thread. Else it opens it first. */
  inConversation: boolean;
  headingRef?: Ref<HTMLHeadingElement>;
  /** A resolution run whose replies the creator validates here (wiki-resolve-with-agent D4). */
  runId?: string | null;
  /** Opens (a run) or leaves (null) the run's replies. */
  onRun?: (runId: string | null) => void;
}) {
  const { project, set, inConversation } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<"publish" | "decide" | "discard" | null>(null);
  const tray = useInstructionTray(set.id);
  const [editing, setEditing] = useState(false);
  // The inline editor's unsaved text holds page switches, navigation and reloads (as the review).
  const [dirty, setDirty] = useState(false);
  const [unsaved, setUnsaved] = useState("");
  const guard = useReviewGuard(dirty, unsaved);
  const leaveEditor = (then: () => void) =>
    guard.confirm(() => {
      setEditing(false);
      setDirty(false);
      then();
    });
  const page = useRef<HTMLDivElement>(null);
  const working = set.status === "open" && set.submittedAt === null;
  const open = set.status === "open" || set.status === "changes_requested";
  const canDraft = canChange(project, "wiki.draft");
  const conversationId = set.provenance.conversation?.id ?? null;
  // Instructions go to the set's own conversation; a set made elsewhere has none to steer.
  const instructs = open && canDraft && conversationId !== null && !set.hiddenItems;
  const content = set.items.filter((i) => !isStructureItem(i));
  const structure = set.items.filter(isStructureItem);
  const selectedId =
    (props.selected === STRUCTURE || structure.some((i) => i.id === props.selected)) &&
    structure.length
      ? STRUCTURE
      : (set.items.find((i) => i.id === props.selected && !isStructureItem(i))?.id ??
        content[0]?.id ??
        (structure.length ? STRUCTURE : null));
  const item = useChangeItem(
    project.id,
    set.id,
    selectedId && selectedId !== STRUCTURE ? selectedId : undefined,
  );
  // The selected page's possible contradictions (D7): the author sees them before an approval
  // that acknowledges them in their name.
  const queue = useReviewQueue(project.id, open && canChange(project, "wiki.view_drafts"));
  const flags = (queue.data?.items ?? [])
    .filter((r) => r.kind === "contradiction" && r.changeItemId && r.changeItemId === selectedId)
    .map((r) => flagOf(r, item.data?.markdown));
  const endpoint = api.api.projects[":projectId"].wiki["change-sets"][":id"];
  const param = { projectId: project.id, id: set.id };
  const { csrfToken: csrf } = useSession();
  const action = useMutation({
    mutationFn: async (kind: "submit" | "discard" | "approveAll") => {
      const h = { headers: { [CSRF_HEADER]: csrf } };
      if (kind === "submit") {
        await ok(await endpoint.submit.$post({ param }, h));
      } else if (kind === "approveAll") {
        const res = await endpoint.approve.$post({ param }, h);
        if ((res.status as number) !== 204) {
          await ok(res);
        }
      } else {
        await noContent(await endpoint.discard.$post({ param }, h));
      }
    },
    onSuccess: (_, kind) => {
      setDialog(kind === "approveAll" ? "decide" : null);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: wikiKeys.all(project.id) }),
  });
  const regenerate = useMutation({
    mutationFn: async (itemId: string) =>
      ok(
        await endpoint.items[":itemId"].regenerate.$post(
          { param: { ...param, itemId } },
          { headers: { [CSRF_HEADER]: csrf } },
        ),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: wikiKeys.all(project.id) }),
  });
  const trust = trustState(set);
  const decides = trust.whole && set.mayPublish !== false && set.trust !== undefined;
  const stale = set.items.some((i) => i.status === "stale");
  const publishable =
    !stale &&
    !set.hiddenItems &&
    !changeRequestState(set).publishBlocked &&
    set.items.some((i) => i.status === "pending" || i.status === "approved");
  const message = tray.items.length ? instructionsMessage(set.title, tray.items) : "";
  // One user message: the conversation's limit holds the whole batch (the tray keeps it).
  const tooLong = message.length > MAX_MESSAGE_CHARS;
  const send = () => {
    if (!conversationId || !tray.items.length || tooLong) {
      return;
    }
    queueInstructions(conversationId, message);
    tray.clear();
    if (!inConversation) {
      navigate(
        withOpenWiki(projectConversationPath(project.id, conversationId), {
          kind: "changes",
          changeSetId: set.id,
          itemId: null,
        }),
      );
    }
  };
  // Resolve with the agent (wiki-resolve-with-agent D1): its turn runs in the set's own
  // conversation, opened first when the preview sits elsewhere.
  const resolve = () => {
    if (!conversationId || !set.resolve) {
      return;
    }
    queueResolve(conversationId, {
      changeSetId: set.id,
      title: set.hiddenItems ? "" : set.title,
      threadCount: set.resolve.count,
    });
    if (!inConversation) {
      navigate(projectConversationPath(project.id, conversationId));
    }
  };
  const decider = whoDecides(set);
  if (props.runId && props.onRun) {
    const onRun = props.onRun;
    return (
      <ResolutionReview
        projectId={project.id}
        set={set}
        runId={props.runId}
        onBack={() => onRun(null)}
      />
    );
  }
  const revision = set.agentRevision;
  return (
    <div data-esi-content="wiki" className="flex min-h-0 flex-col">
      <header className="flex flex-col gap-2 border-b border-line px-4 py-3.5 sm:px-5">
        <div className="flex flex-wrap items-center gap-2">
          <Eye aria-hidden="true" className="size-4 text-fg-muted" strokeWidth={1.75} />
          <h2
            ref={props.headingRef}
            tabIndex={-1}
            className="min-w-0 flex-1 text-[15px] font-semibold tracking-tight break-words text-fg outline-none"
          >
            {t("wiki.author.previewTitle")}
            <span className="sr-only">: {set.title}</span>
          </h2>
          {/* Publishing or submitting while the editor holds unsaved text would leave it behind. */}
          {working && canDraft && !editing ? (
            decides ? (
              <>
                <Button
                  size="sm"
                  disabled={!publishable || action.isPending}
                  onClick={() => setDialog("publish")}
                >
                  <Check aria-hidden="true" strokeWidth={1.75} />
                  {t("wiki.author.publish")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-fg-secondary"
                  disabled={action.isPending}
                  onClick={() => action.mutate("submit")}
                >
                  {t("wiki.author.submitInstead")}
                </Button>
              </>
            ) : (
              <Button size="sm" disabled={action.isPending} onClick={() => action.mutate("submit")}>
                <Send aria-hidden="true" strokeWidth={1.75} />
                {t("wiki.author.submit")}
              </Button>
            )
          ) : null}
          {!working && open && canDraft && decides && !editing ? (
            <Button
              size="sm"
              disabled={!publishable || action.isPending}
              onClick={() => action.mutate("approveAll")}
            >
              <Check aria-hidden="true" strokeWidth={1.75} />
              {t("wiki.author.approvePublish")}
            </Button>
          ) : null}
          {open && canDraft ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-fg-secondary"
              disabled={action.isPending}
              onClick={() => setDialog("discard")}
            >
              <X aria-hidden="true" strokeWidth={1.75} />
              {t("wiki.card.discard")}
            </Button>
          ) : null}
        </div>
        <p className="text-xs text-fg-secondary">
          <span className="font-medium text-fg">{set.title}</span>
          {decider && open ? ` · ${decider}` : ""}
        </p>
        {set.rationale && !set.hiddenItems ? (
          // Why, as its author wrote it (the page editor's "Why", an agent's rationale).
          <p className="max-w-prose text-[13px] break-words text-fg-secondary">{set.rationale}</p>
        ) : null}
        {set.provenance.mcp && !set.hiddenItems ? (
          // Which harness proposed it, and its summary.
          <p className="text-xs text-fg-secondary">
            {set.provenance.mcp.clientHost} · {set.provenance.mcp.summary}
          </p>
        ) : null}
        {open ? (
          <p className="text-xs text-fg-muted">
            {working ? t("wiki.author.onlyYou") : t("wiki.author.readOnly")}
          </p>
        ) : null}
        {open && canDraft && conversationId && !editing ? (
          <ResolveTrigger set={set} busy={false} onResolve={resolve} />
        ) : null}
        {(revision?.status === "drafted" || revision?.status === "failed") && props.onRun ? (
          // The latest run's drafts wait for their author (D4).
          <p className="flex flex-wrap items-center gap-x-2 text-xs text-fg-secondary">
            <span>{t("wiki.resolve.bar.drafted", { revision: revision.revision })}</span>
            <button
              type="button"
              className="text-accent-text underline-offset-4 hover:underline"
              onClick={() => props.onRun?.(revision.runId)}
            >
              {t("wiki.resolve.review")}
            </button>
          </p>
        ) : null}
        {set.mcpPublisher === "others" && open ? (
          // A harness's proposal its creator may not publish (#152).
          <p className="text-xs text-fg-secondary">{t("wiki.changes.mcpPublisher.notYou")}</p>
        ) : null}
        {stale && open ? (
          // Why Publish waits: the pages that changed since (regenerate or edit them).
          <Notice tone="warning">
            {t("wiki.changes.blockedStale", {
              pages: set.items
                .filter((i) => i.status === "stale")
                .map((i) => i.title)
                .join(", "),
            })}
          </Notice>
        ) : null}
        {action.isError ? <Notice tone="danger">{action.error.message}</Notice> : null}
      </header>
      <nav aria-label={t("wiki.author.changedPages")} className="border-b border-line">
        <ul className="flex gap-1 overflow-x-auto px-3 py-2 sm:px-4">
          {content.map((entry) => {
            const size = itemSize(entry);
            return (
              <li key={entry.id} className="shrink-0">
                <button
                  type="button"
                  aria-current={entry.id === selectedId ? "true" : undefined}
                  onClick={() => leaveEditor(() => props.onSelect(entry.id))}
                  className={cn(
                    "flex max-w-56 items-center gap-1.5 rounded-md px-2.5 py-1 text-xs text-fg-secondary hover:bg-surface-hover",
                    entry.id === selectedId && "bg-surface-selected text-fg",
                  )}
                >
                  <span className="truncate">{itemName(entry)}</span>
                  {size ? (
                    <span className="shrink-0 text-fg-secondary tabular-nums">{size}</span>
                  ) : null}
                </button>
              </li>
            );
          })}
          {structure.length ? (
            <li className="shrink-0">
              <button
                type="button"
                aria-current={selectedId === STRUCTURE ? "true" : undefined}
                onClick={() => leaveEditor(() => props.onSelect(STRUCTURE))}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs text-fg-secondary hover:bg-surface-hover",
                  selectedId === STRUCTURE && "bg-surface-selected text-fg",
                )}
              >
                <FolderTree aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
                {t("wiki.author.structure")}
              </button>
            </li>
          ) : null}
        </ul>
      </nav>
      <div ref={page} className="relative flex flex-col gap-4 px-4 py-4 sm:px-5">
        {selectedId === STRUCTURE ? (
          <section aria-label={t("wiki.author.structure")} className="flex flex-col gap-3">
            {structure.map((entry) => (
              <div key={entry.id} className="flex flex-col gap-1.5">
                <h3 className="text-sm font-medium break-words text-fg">{itemName(entry)}</h3>
                <ItemContent projectId={project.id} item={entry} />
                {/* A move or folder keeps its discussion: a change request on it blocks
                    publication and is answered here. */}
                <ThreadPanel
                  projectId={project.id}
                  set={set}
                  itemId={entry.id}
                  canComment={canChange(project, "wiki.view_drafts")}
                />
              </div>
            ))}
          </section>
        ) : !selectedId ? null : item.isError ? (
          <Notice tone="danger">{item.error.message}</Notice>
        ) : !item.data ? (
          <Skeleton className="h-40" />
        ) : (
          <section aria-label={itemName(item.data)} className="flex flex-col gap-3">
            <h3
              data-selection-ignore=""
              className="text-lg font-semibold tracking-tight break-words text-fg"
            >
              {item.data.title}
            </h3>
            {flags.length ? (
              <Notice tone="warning" title={t("wiki.review.margin.flag")}>
                <ul data-esi-content="wiki" className="flex flex-col gap-1">
                  {flags.map((flag) => (
                    <li key={flag.id}>
                      {flag.statement ? `“${flag.statement}” · ` : ""}
                      {flag.explanation}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs">{t("wiki.review.margin.flagHint")}</p>
              </Notice>
            ) : null}
            {item.data.staleReason ? (
              <Notice tone="warning">
                {isFolderOp(item.data.op)
                  ? t("wiki.changes.folderMissing")
                  : t(`wiki.changes.stale.${item.data.staleReason}`)}
              </Notice>
            ) : null}
            {open && canDraft && !editing ? (
              // The author's own item actions: the inline editor, and a stale edit regenerated
              // against the current page (never an approval or a rejection).
              <div data-selection-ignore="" className="flex flex-wrap gap-1.5">
                {["create", "edit", "rename"].includes(item.data.op) ? (
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                    <Pencil aria-hidden="true" strokeWidth={1.75} />
                    {t("wiki.changes.edit")}
                  </Button>
                ) : null}
                {item.data.status === "stale" && item.data.op === "edit" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={regenerate.isPending}
                    onClick={() => regenerate.mutate(item.data?.id ?? "")}
                  >
                    {t("wiki.changes.regenerate")}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {regenerate.isError ? <Notice tone="danger">{regenerate.error.message}</Notice> : null}
            {editing ? (
              <ChangeItemEditor
                key={item.data.id}
                project={project}
                item={item.data}
                id={set.id}
                onDirty={setDirty}
                onTextChange={setUnsaved}
                onDone={() => {
                  setEditing(false);
                  setDirty(false);
                  void queryClient.invalidateQueries({ queryKey: wikiKeys.all(project.id) });
                }}
                onCancel={() => leaveEditor(() => {})}
              />
            ) : item.data.op === "create" || item.data.op === "edit" ? (
              <MarkedPage
                base={item.data.op === "edit" ? (item.data.baseMarkdown ?? null) : null}
                proposed={item.data.markdown ?? ""}
                baseVersion={item.data.baseVersion}
                title={item.data.title}
                view={props.view}
                onView={props.onView}
                headingPrefix={`preview-${item.data.id.slice(0, 8)}`}
              />
            ) : (
              <ItemContent projectId={project.id} item={item.data} />
            )}
          </section>
        )}
        {instructs && selectedId !== STRUCTURE && item.data ? (
          <SelectionPopover
            // A new page starts a new selection: nothing typed follows another page.
            key={selectedId}
            mode="instruct"
            container={page}
            disabled={tray.full ? t("wiki.author.instruct.full") : undefined}
            onInstruct={(passage, text) =>
              tray.add({
                quote: passage.quote,
                page: item.data?.title ?? "",
                section: passage.section?.text ?? null,
                text,
              })
            }
          />
        ) : null}
      </div>
      {instructs && tray.items.length ? (
        <InstructionTray
          items={tray.items}
          tooLong={tooLong}
          onRemove={tray.remove}
          onClear={tray.clear}
          onSend={send}
        />
      ) : null}
      <AppDialog
        open={dialog === "discard"}
        onClose={() => setDialog(null)}
        title={t("wiki.card.discardTitle")}
      >
        <p className="text-sm text-fg-secondary">{t("wiki.card.discardBody")}</p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setDialog(null)}>
            {t("wiki.editor.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={action.isPending}
            onClick={() => action.mutate("discard")}
          >
            {t("wiki.card.discard")}
          </Button>
        </DialogFooter>
      </AppDialog>
      <AuthorPublishSheet
        open={dialog === "publish"}
        onClose={() => setDialog(null)}
        project={project}
        changeSet={set}
        // A page that could be published wider: the full dialog, where a manager declassifies.
        onDeclassify={() => setDialog("decide")}
      />
      {guard.dialog}
      <ChangeSetPublishDialog
        open={dialog === "decide"}
        onClose={() => setDialog(null)}
        project={project}
        changeSet={set}
      />
    </div>
  );
}

/** The instructions collected for the agent (mock S2): only the author sees them; Send is one turn. */
function InstructionTray(props: {
  items: readonly { id: string; quote: string; text: string }[];
  tooLong: boolean;
  onRemove: (id: string) => void;
  onClear: () => void;
  onSend: () => void;
}) {
  const { t } = useTranslation();
  return (
    <section
      data-esi-content="wiki"
      aria-label={t("wiki.author.tray.title", { count: props.items.length })}
      data-selection-ignore=""
      className="sticky bottom-0 flex flex-col gap-2.5 border-t border-dashed border-line-strong bg-surface px-4 py-3 sm:px-5"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-[13px] font-medium text-fg">
          <Bot aria-hidden="true" className="size-4 text-fg-muted" strokeWidth={1.75} />
          {t("wiki.author.tray.title", { count: props.items.length })}
        </h3>
        <span className="text-xs text-fg-muted">{t("wiki.author.tray.onlyYou")}</span>
      </div>
      <ul className="flex flex-col gap-1.5">
        {props.items.map((item) => (
          <li
            key={item.id}
            className="flex items-start gap-2 rounded-md border border-dashed border-line-strong px-2.5 py-1.5 text-xs"
          >
            <TextQuote
              aria-hidden="true"
              className="mt-0.5 size-3.5 shrink-0 text-fg-muted"
              strokeWidth={1.75}
            />
            <span className="min-w-0 flex-1 break-words">
              <q className="text-fg-secondary">{item.quote}</q>{" "}
              <span className="text-fg">{item.text}</span>
            </span>
            <button
              type="button"
              aria-label={t("wiki.author.tray.remove", { quote: item.quote.slice(0, 60) })}
              onClick={() => props.onRemove(item.id)}
              className="rounded p-0.5 text-fg-muted hover:bg-surface-hover hover:text-fg"
            >
              <X aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
            </button>
          </li>
        ))}
      </ul>
      {props.tooLong ? (
        <p className="text-xs text-danger-fg">{t("wiki.author.tray.tooLong")}</p>
      ) : null}
      <div className="flex justify-end gap-1.5">
        <Button type="button" size="sm" variant="ghost" onClick={props.onClear}>
          {t("wiki.author.tray.clear")}
        </Button>
        <Button type="button" size="sm" disabled={props.tooLong} onClick={props.onSend}>
          <Send aria-hidden="true" strokeWidth={1.75} />
          {t("wiki.author.tray.send")}
        </Button>
      </div>
    </section>
  );
}
